import { API_ROUTES, platformStatusSchema, type ComponentState } from '@hvac/contracts';
import { Skeleton } from '@hvac/ui';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/queries.js';

/**
 * The public status page.
 *
 * Three plain answers, refreshed every thirty seconds: do the books open, is
 * the data there, are new documents being read on time. Public, so a firm
 * can check it without signing in, and it says nothing about any business.
 */

const LABELS: Readonly<Record<ComponentState, string>> = {
  operational: 'Working normally',
  slow: 'Slower than usual',
  delayed: 'Delayed',
  down: 'Not working',
  off: 'Not switched on',
};

const DOTS: Readonly<Record<ComponentState, string>> = {
  operational: 'bg-success',
  slow: 'bg-warning',
  delayed: 'bg-warning',
  down: 'bg-danger',
  off: 'bg-content-subtle',
};

const HEADLINES: Readonly<Record<ComponentState, string>> = {
  operational: 'Everything is working.',
  slow: 'Working, but reading documents is slower than usual.',
  delayed: 'Working, but reading new documents is delayed.',
  down: 'Something is not working. It is being looked at.',
  off: 'Everything is working.',
};

export function StatusPage() {
  const status = useQuery({
    queryKey: ['platform-status'],
    queryFn: ({ signal }) =>
      apiClient.request({ path: API_ROUTES.health.status, schema: platformStatusSchema, signal }),
    refetchInterval: 30_000,
    retry: false,
  });

  const unreachable = status.isError;
  const overall: ComponentState = unreachable ? 'down' : (status.data?.overall ?? 'operational');

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-xl flex-col px-4 py-12 sm:px-6">
      <p className="mb-10 flex items-center gap-2.5">
        <span className="flex size-8 items-center justify-center rounded-[9px] bg-gradient-to-br from-brand-500 to-brand-700 text-sm font-bold text-white">
          P
        </span>
        <span className="text-sm font-semibold tracking-tight text-content">
          Pathara <span className="text-brand-600">Finance</span> status
        </span>
      </p>

      {status.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <>
          <h1 className="flex items-center gap-3 text-2xl font-semibold tracking-tight text-content">
            <span className={`size-3 rounded-full ${DOTS[overall]}`} aria-hidden />
            {unreachable ? 'The service cannot be reached.' : HEADLINES[overall]}
          </h1>
          <ul className="mt-8 divide-y divide-border rounded-[var(--radius-surface)] border border-border bg-surface">
            {(status.data?.components ?? []).map((component) => (
              <li key={component.key} className="flex flex-col gap-1 px-4 py-3">
                <span className="flex items-center justify-between gap-4 text-sm">
                  <span className="font-medium text-content">{component.name}</span>
                  <span className="flex items-center gap-2 text-content-muted">
                    <span className={`size-2 rounded-full ${DOTS[component.state]}`} aria-hidden />
                    {LABELS[component.state]}
                  </span>
                </span>
                {component.detail === null ? null : (
                  <span className="text-xs text-content-muted">{component.detail}</span>
                )}
              </li>
            ))}
            {unreachable ? (
              <li className="px-4 py-3 text-sm text-content-muted">
                The server did not answer. If this lasts more than a few minutes, it is a real
                outage; your data is safe in the database either way.
              </li>
            ) : null}
          </ul>
          <p className="mt-4 text-xs text-content-subtle">
            Checked{' '}
            {status.data === undefined
              ? 'just now'
              : new Date(status.data.checkedAt).toLocaleTimeString()}
            . Refreshes every thirty seconds.
          </p>
        </>
      )}
    </main>
  );
}
